import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { FaGoogle, FaGithub, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Auth = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLogin, setIsLogin] = useState(false);
    const { login } = useAuth();

    // Google OAuth configuration
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const REDIRECT_URI = `${window.location.origin}/auth/callback`;
    
    const handleGoogleLogin = () => {
        setLoading(true);
        setError('');
        
        const googleAuthUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
        const scope = 'openid email profile';
        
        const params = new URLSearchParams({
            client_id: GOOGLE_CLIENT_ID,
            redirect_uri: REDIRECT_URI,
            response_type: 'code',
            scope: scope,
            access_type: 'offline',
            prompt: 'consent',
            state: 'google'
        });
        
        window.location.href = `${googleAuthUrl}?${params.toString()}`;
    };

    const handleGithubLogin = () => {
        setLoading(true);
        setError('');
        
        const githubAuthUrl = 'https://github.com/login/oauth/authorize';
        const scope = 'user:email';
        
        const params = new URLSearchParams({
            client_id: process.env.REACT_APP_GITHUB_CLIENT_ID,
            redirect_uri: REDIRECT_URI,
            scope: scope,
            state: 'github'
        });
        
        window.location.href = `${githubAuthUrl}?${params.toString()}`;
    };

    const handleEmailSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            const endpoint = isLogin ? '/auth/email-login' : '/auth/email-signup';
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || (isLogin ? 'Login failed' : 'Signup failed'));
            }
            
            // Store token and redirect
            login(data.token, data.user);
            window.location.href = '/welcome';
            
        } catch (err) {
            setError(err.message || `Failed to ${isLogin ? 'login' : 'signup'}. Please try again.`);
            setLoading(false);
        }
    };

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={6} lg={5}>
                    <Card className="border-0 shadow">
                        <Card.Body className="p-5">
                            <div className="text-center mb-4">
                                <h2 className="mb-3">{isLogin ? 'Welcome Back' : 'Login to Start Your Free Trial'}</h2>
                                <p className="text-muted">
                                    {isLogin ? 'Login to access your account' : 'Get instant access to curated AI tools that make your day better'}
                                </p>
                            </div>

                            {error && (
                                <Alert variant="danger" className="mb-4">
                                    {error}
                                </Alert>
                            )}

                            <div className="d-grid gap-3 mb-4">
                                <Button
                                    variant="outline-primary"
                                    className="d-flex align-items-center justify-content-center"
                                    onClick={handleGoogleLogin}
                                    disabled={loading || !GOOGLE_CLIENT_ID}
                                >
                                    <FaGoogle className="me-2" />
                                    Continue with Google
                                </Button>

                                <Button
                                    variant="outline-dark"
                                    className="d-flex align-items-center justify-content-center"
                                    onClick={handleGithubLogin}
                                    disabled={loading || !process.env.REACT_APP_GITHUB_CLIENT_ID}
                                >
                                    <FaGithub className="me-2" />
                                    Continue with GitHub
                                </Button>
                            </div>

                            <div className="text-center mb-4">
                                <span className="text-muted">or</span>
                            </div>

                            <form onSubmit={handleEmailSignup}>
                                <div className="mb-3">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaEnvelope />
                                        </span>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="Enter your email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={loading}
                                        minLength="6"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-100"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            {isLogin ? 'Logging in...' : 'Starting trial...'}
                                        </>
                                    ) : (
                                        isLogin ? 'Login' : 'Start Free Trial'
                                    )}
                                </Button>
                            </form>

                            <div className="text-center mt-4">
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none"
                                    onClick={() => {
                                        setIsLogin(!isLogin);
                                        setError('');
                                    }}
                                    disabled={loading}
                                >
                                    {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
                                </button>
                            </div>

                            {!isLogin && (
                                <div className="text-center mt-2">
                                    <small className="text-muted">
                                        7-day free trial • $3.99/month after • Cancel anytime
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Auth;
